import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, Separator, Tabs} from 'platform/components';
import {
  Box,
  CSSDimension,
  getSize,
  Grid,
  GridItem,
  Hide,
  Scroll,
  Show,
  useDevice,
  VStack,
} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {includes, isNotNil} from 'ramda';

import {
  selectTenant,
  useGetSourcingAuctionDetailQuery,
  useSourcingVehicleDetailQuery,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {formatDocumentTitle, is404ApiError, MAIN_TOPBAR_HEIGHT} from '@dms/shared';

import {pushGtmEvent, useOnMount, useQueryState} from 'shared/web';

import {CommentsTab} from '../(sections)/CommentsTab/CommentsTab';
import {CostsTab} from '../(sections)/CostsTab/CostsTab';
import {DrillDownTab} from '../(sections)/DrillDownTab/DrillDownTab';
import {FullScreenDetailLayout} from '../(sections)/FullScreenDetailLayout/FullScreenDetailLayout';
import {HighlightsTab} from '../(sections)/HighlightsTab/HighlightsTab';
import {PriceMapTab} from '../(sections)/PriceMapTab/PriceMapTab';
import {AdvanceAuctionView} from '../../../components/AdvanceAuctionView/AdvanceAuctionView';
import {AdvanceVehicleView} from '../../../components/AdvanceVehicleView/AdvanceVehicleView';
import {AuctionPreview} from '../../../components/AuctionPreview/AuctionPreview';
import {
  ResizablePreviewLayout,
  type ResizablePreviewOptions,
} from '../../../components/SourcingLayout/components/ResizablePreviewLayout';
import {VehiclePreview} from '../../../components/VehiclePreview/VehiclePreview';
import {VehiclePriceForm} from '../../../components/VehiclePriceForm/VehiclePriceForm';
import {sourcingStorageKey} from '../../../constants/sourcingStorageKey';
import {useAuctionEnumLookup} from '../../../hooks/useAuctionEnumLookup';
import {useIsSourcingCardViewEnforced} from '../../../hooks/useIsSourcingCardViewEnforced';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {useSourcingVehicleAvailability} from '../../../hooks/useSourcingVehicleAvailability';
import {adaptAuctionVehicleToSourcingItem} from '../../../utils/adaptAuctionVehicleToSourcingItem';
import {getDetailFirstLine} from '../../../utils/getDetailFirstLine';
import {isClassifiedsAdInactive} from '../../../utils/isClassifiedsAdInactive';
import {isEditingTextTarget} from '../../../utils/isEditingTextTarget';
import {isUnifiedSourcingEnabled} from '../../../utils/isUnifiedSourcingEnabled';

const TAB_QUERY_KEY = 'tab';
const DEFAULT_ACTIVE_TAB = 'drillDown';
const COLUMN_HEIGHT: CSSDimension = `calc(100vh - ${getSize(MAIN_TOPBAR_HEIGHT)})`;
/**
 * The detail page keeps its own split - it is usually read with a wider advertisement than the
 * vehicle list, the left column holds a form that must not be squeezed, and the page scrolls
 * itself instead of filling a container of a fixed height.
 */
const DETAIL_PREVIEW_OPTIONS: ResizablePreviewOptions = {
  storageKey: sourcingStorageKey.DETAIL_PREVIEW_LAYOUT_PREFIX,
  minWidth: 25,
  maxWidth: 50,
  isPreviewShownOnNotebook: true,
  isHeightAuto: true,
};

const UNIFIED_FORM_CARD_PROPS = {
  padding: 4,
  backgroundColor: 'general.white',
  borderTop: '1px solid',
  borderColor: 'palettes.neutral.30.100',
  borderBottomLeftRadius: 'small',
  borderBottomRightRadius: 'small',
  boxShadow: 'elevation_1',
} as const;

// eslint-disable-next-line complexity -- Pre-existing violation
export function VehicleDetailPage() {
  const enhancedSurfaces = isUnifiedSourcingEnabled();
  const {adId} = useParams<{adId?: string}>();
  const sourcingType = useSourcingType();
  const enumLookup = useAuctionEnumLookup();
  const device = useDevice();
  // The ad preview is usually much taller than the detail itself, so scrolling it used to push the
  // whole detail column out of the viewport. In the side by side layout the detail column sticks to
  // the top instead and only scrolls when its own content does not fit.
  const hasStickyDetailColumn = includes(device, ['notebook', 'desktop']);
  const isCardViewEnforced = useIsSourcingCardViewEnforced();

  const validTabIds: string[] = ['drillDown', 'priceMap', 'costs', 'highlights', 'comments'];
  const [tabIdFromUrl, setTabId] = useQueryState(TAB_QUERY_KEY);
  const tabId =
    tabIdFromUrl && validTabIds.includes(tabIdFromUrl) ? tabIdFromUrl : DEFAULT_ACTIVE_TAB;
  const [fullScreen, setFullScreen] = useState(false);

  useOnMount(() => {
    const detailTab = tabId.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    const event = `sourcing_${sourcingType}_${detailTab}_viewed`;
    pushGtmEvent({
      event,
      event_id: event,
      params: {
        detail_tab: tabId,
        sourcing_type: sourcingType,
      },
    });
  });

  const {data: selectedTenant} = useSelector(selectTenant);
  const tenantCountry = selectedTenant?.country;
  const currency = useSourcingCurrency()?.code;
  const country = useSourcingTargetCountry()?.code ?? tenantCountry;
  const pricingStrategy = useSourcingPricingStrategy();

  useEffect(function bindBackspaceHistoryNavigation() {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace') {
        return;
      }
      if (isEditingTextTarget(e.target)) {
        return;
      }
      e.preventDefault();
      window.history.back();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const {
    data: classifiedsVehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
    error: vehicleError,
  } = useSourcingVehicleDetailQuery(
    isNotNil(adId)
      ? {externalId: adId, currency, buyerCountry: country, pricingStrategy}
      : skipToken,
    {skip: sourcingType === SourcingType.AUCTIONS}
  );
  const {
    data: auctionVehicle,
    isLoading: isAuctionVehicleLoading,
    isError: isAuctionVehicleError,
    error: auctionVehicleError,
  } = useGetSourcingAuctionDetailQuery(
    isNotNil(adId)
      ? {externalId: adId, currency, buyerCountry: country, pricingStrategy}
      : skipToken,
    {skip: sourcingType === SourcingType.CLASSIFIEDS}
  );
  const auctionVehicleAdapted = isNotNil(auctionVehicle)
    ? adaptAuctionVehicleToSourcingItem(auctionVehicle, enumLookup)
    : undefined;
  const vehicle =
    sourcingType === SourcingType.CLASSIFIEDS ? classifiedsVehicle : auctionVehicleAdapted;
  const isLoading =
    sourcingType === SourcingType.CLASSIFIEDS ? isVehicleLoading : isAuctionVehicleLoading;
  const isError =
    sourcingType === SourcingType.CLASSIFIEDS ? isVehicleError : isAuctionVehicleError;
  const error = sourcingType === SourcingType.CLASSIFIEDS ? vehicleError : auctionVehicleError;

  const isVehicleNotFound = is404ApiError(error);
  const errorMessage = isVehicleNotFound
    ? i18n.t('page.vehicle.notifications.notFound')
    : undefined;

  const isAvailabilityCheckEnabled =
    isFeatureEnabled(featureFlags.SOURCING_AVAILABILITY_CHECK) &&
    sourcingType === SourcingType.CLASSIFIEDS &&
    isNotNil(classifiedsVehicle);

  // Side-effect only: poll + patch `justSold` onto detail/list caches (no checking/active Flags).
  useSourcingVehicleAvailability({
    adId,
    enabled: isAvailabilityCheckEnabled,
  });

  const tabs = [
    {
      id: 'drillDown',
      title: i18n.t('general.labels.drillDown'),
      content: <DrillDownTab vehicle={vehicle} setFullScreen={setFullScreen} />,
    },
    {
      id: 'priceMap',
      title: i18n.t('general.labels.priceMap'),
      // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
      content: isNotNil(vehicle) && (
        <PriceMapTab
          key={vehicle.sourcingVehicle.adId}
          vehicle={vehicle}
          data-testid={testIds.sourcing.vehicleDetail('priceMapTab')}
        />
      ),
    },
    {
      id: 'costs',
      title: i18n.t('general.labels.costs'),
      content: isNotNil(adId) && isNotNil(vehicle) && <CostsTab id={adId} vehicle={vehicle} />, // eslint-disable-line eag/prefer-show -- Pre-existing violation
    },
    {
      id: 'highlights',
      title: i18n.t('entity.vehicle.labels.highlighted'),
      // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
      content: isNotNil(adId) && isNotNil(vehicle) && (
        <HighlightsTab adId={adId} vehicle={vehicle} />
      ),
    },
    {
      id: 'comments',
      title: i18n.t('page.comments.labels.comments'),
      // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
      content: isNotNil(adId) && (
        <CommentsTab
          externalId={adId}
          data-testid={testIds.sourcing.vehicleDetail('commentsTab')}
        />
      ),
    },
  ];

  const advanceView =
    sourcingType === SourcingType.AUCTIONS ? (
      <AdvanceAuctionView
        isDetail={enhancedSurfaces}
        vehicle={auctionVehicleAdapted}
        isVehicleLoading={isLoading}
        data-testid={testIds.sourcing.vehicleDetail('advancedAuctionView')}
      />
    ) : (
      <AdvanceVehicleView
        isDetail={enhancedSurfaces}
        vehicle={vehicle}
        isVehicleLoading={isLoading}
        data-testid={testIds.sourcing.vehicleDetail('advancedVehicleView')}
      />
    );
  const priceForm = (
    <VehiclePriceForm
      vehicleId={adId}
      isDisabled={
        sourcingType === SourcingType.CLASSIFIEDS &&
        isClassifiedsAdInactive(vehicle?.sourcingVehicle)
      }
      initialValues={{
        purchasePrice: vehicle?.vehicleSummary?.purchasePrice?.toString() ?? undefined,
        sellingPrice: vehicle?.vehicleSummary?.sellingPrice?.toString() ?? undefined,
        comment: vehicle?.vehicleSummary?.comment ?? undefined,
      }}
      data-testid={testIds.sourcing.vehicleDetail('vehiclePriceForm')}
    />
  );

  const detailColumn = (
    <Box
      backgroundColor={enhancedSurfaces ? 'palettes.neutral.20.100' : undefined}
      position={hasStickyDetailColumn ? 'sticky' : undefined}
      top={0}
    >
      <Scroll
        height={hasStickyDetailColumn ? COLUMN_HEIGHT : undefined}
        auto={hasStickyDetailColumn ? true : undefined}
      >
        <DataStatus
          minHeight="100%"
          isLoading={isLoading}
          isError={isError}
          errorMessage={errorMessage}
        >
          {enhancedSurfaces ? (
            <Box paddingHorizontal={4} paddingTop={4}>
              {advanceView}
            </Box>
          ) : (
            advanceView
          )}
          <Box
            paddingHorizontal={4}
            paddingTop={enhancedSurfaces ? undefined : 2}
            paddingBottom={enhancedSurfaces ? 4 : undefined}
          >
            {enhancedSurfaces ? <Box {...UNIFIED_FORM_CARD_PROPS}>{priceForm}</Box> : priceForm}
            <TabsWrapper $hasScrollableTabStrip={isCardViewEnforced}>
              <Tabs
                tabs={tabs}
                activeTabId={tabId}
                onChange={setTabId}
                data-testid={testIds.sourcing.vehicleDetail('vehicleDetailPage-tabs')}
              />
            </TabsWrapper>
          </Box>
        </DataStatus>
      </Scroll>
    </Box>
  );

  // Deliberately not tied to the loaded vehicle - the two branches render different wrappers, so
  // switching between them while the detail is loading would remount the whole column and throw
  // away unsaved prices. The preview itself is passed only once there is something to show.
  const isResizableLayout =
    isFeatureEnabled(featureFlags.SOURCING_RESIZABLE_PREVIEW) && hasStickyDetailColumn;

  const previewColumn = (
    <VStack minWidth={0}>
      <Hide onNotebook onDesktop>
        <Separator />
      </Hide>
      {sourcingType === SourcingType.AUCTIONS && isNotNil(auctionVehicle) ? (
        <AuctionPreview
          auction={auctionVehicle}
          isScrollDisabled
          data-testid={testIds.sourcing.vehicleDetail('auctionPreview')}
        />
      ) : (
        <VehiclePreview vehicleId={adId} isScrollDisabled />
      )}
    </VStack>
  );

  const content = (
    <>
      <Helmet
        title={formatDocumentTitle(
          (isNotNil(vehicle) && getDetailFirstLine(vehicle?.sourcingVehicle)) ||
            i18n.t('entity.vehicle.labels.vehicle'),
          i18n.t('page.sourcing.labels.sourcing')
        )}
      />
      <Show when={fullScreen}>
        <FullScreenDetailLayout vehicle={vehicle} onClose={() => setFullScreen(false)} />
      </Show>
      <Hide when={fullScreen}>
        <VStack height="100%">
          <Show when={isResizableLayout}>
            <ResizablePreviewLayout
              preview={isNotNil(vehicle) ? previewColumn : undefined}
              options={DETAIL_PREVIEW_OPTIONS}
              data-testid={testIds.sourcing.vehicleDetail('resizableLayout')}
            >
              {detailColumn}
            </ResizablePreviewLayout>
          </Show>
          <Hide when={isResizableLayout}>
            <Grid columns={isNotNil(vehicle) ? [1, 1, 3, 3] : 1} spacing={0} grow={1}>
              <GridItem span={isNotNil(vehicle) ? [1, 1, 2, 2] : 1}>{detailColumn}</GridItem>
              <Show when={isNotNil(vehicle)}>{previewColumn}</Show>
            </Grid>
          </Hide>
        </VStack>
      </Hide>
    </>
  );

  return enhancedSurfaces ? (
    <Box height="100%" backgroundColor="palettes.neutral.20.100">
      {content}
    </Box>
  ) : (
    content
  );
}

// Force Tabs component and all its tabs to have 100% height
// eslint-disable-next-line eag/no-css-property
const TabsWrapper = styled.div<{$hasScrollableTabStrip: boolean}>`
  height: 100%;

  /* Scoped to the card view: a non-visible overflow-x makes overflow-y compute to auto too, so
     the strip would clip vertically (tooltips, dropdowns, focus rings) where the tabs fit. */
  ${({$hasScrollableTabStrip}) =>
    $hasScrollableTabStrip &&
    // eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
    css`
      & > :first-child {
        overflow-x: auto;
      }
    `}

  & > :last-child {
    height: 100%;

    & > * {
      height: 100%;
    }
  }
`;
